import React, { useEffect, useMemo, useState } from 'react';
import { HiOutlineDocumentAdd, HiOutlineDuplicate, HiCheck } from 'react-icons/hi';
import communicationApi from '../../../../../services/communicationApi';
import SearchFilter from '../../../../atoms/SearchFilter';
import {
    CHANNELS,
    CHANNEL_ADAPTER,
    CHANNEL_LABELS,
    ChannelType,
    MessageComposition,
    Template,
} from '../../../../../types/communication';
import { CHANNEL_VISUALS, ChannelTile } from '../shared/channelVisuals';

interface ChannelPickerProps {
    onStart: (channel: ChannelType, source?: { meta: MessageComposition; templates: Template[] }) => void;
}

const ChannelPicker: React.FC<ChannelPickerProps> = ({ onStart }) => {
    const [channel, setChannel] = useState<ChannelType | null>(null);
    const [origin, setOrigin] = useState<'BLANK' | 'COPY'>('BLANK');
    const [metas, setMetas] = useState<MessageComposition[]>([]);
    const [templates, setTemplates] = useState<Template[]>([]);
    const [sourceId, setSourceId] = useState<string | null>(null);
    const [search, setSearch] = useState('');

    useEffect(() => {
        communicationApi.listMessageCompositions({ pageSize: 9999 }).then((r) => setMetas(r.list));
        communicationApi.listTemplates({ pageSize: 9999 }).then((r) => setTemplates(r.list));
    }, []);

    const candidates = useMemo(() => {
        const term = search.trim().toLocaleLowerCase('pt-BR');
        return metas
            .filter((m) => m.channel === channel)
            .filter((m) => !term || `${m.name} ${m.description}`.toLocaleLowerCase('pt-BR').includes(term));
    }, [metas, channel, search]);

    const begin = () => {
        if (!channel) return;
        const source = origin === 'COPY' ? metas.find((m) => m.id === sourceId) : undefined;
        if (source) {
            onStart(channel, { meta: source, templates: templates.filter((t) => source.templateIds.includes(t.id)) });
        } else {
            onStart(channel);
        }
    };

    const ready = channel && (origin === 'BLANK' || sourceId);

    return (
        <div className="max-w-4xl mx-auto py-10">
            <div className="text-center mb-8">
                <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">Que tipo de mensagem você quer criar?</h2>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                    O canal define o editor, os campos de envio e como a mensagem aparece para quem recebe.
                </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {CHANNELS.map((c) => {
                    const selected = channel === c;
                    return (
                        <button
                            key={c}
                            type="button"
                            onClick={() => {
                                setChannel(c);
                                setSourceId(null);
                            }}
                            className={`relative text-left rounded-2xl border p-5 transition-all bg-white dark:bg-slate-800 hover:shadow-md ${
                                selected
                                    ? `ring-2 ${CHANNEL_VISUALS[c].ring} border-transparent shadow-md`
                                    : 'border-gray-200 dark:border-slate-700'
                            }`}
                        >
                            {selected && (
                                <span className="absolute top-3 right-3 w-5 h-5 rounded-full bg-teal-500 text-white flex items-center justify-center">
                                    <HiCheck size={14} />
                                </span>
                            )}
                            <ChannelTile channel={c} size="lg" />
                            <div className="mt-4 font-semibold text-gray-900 dark:text-gray-100">{CHANNEL_LABELS[c]}</div>
                            <div className="text-[11px] uppercase tracking-wide text-gray-400">via {CHANNEL_ADAPTER[c]}</div>
                            <p className="text-sm text-gray-600 dark:text-gray-300 mt-2">{CHANNEL_VISUALS[c].tagline}</p>
                            <p className="text-xs text-gray-400 mt-2 italic">{CHANNEL_VISUALS[c].example}</p>
                        </button>
                    );
                })}
            </div>

            {channel && (
                <div className="mt-8 rounded-2xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-5">
                    <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-100 mb-3">Como quer começar?</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {[
                            { key: 'BLANK' as const, icon: <HiOutlineDocumentAdd size={20} />, title: 'Do zero', text: 'Um rascunho pronto para editar, com estrutura sugerida.' },
                            { key: 'COPY' as const, icon: <HiOutlineDuplicate size={20} />, title: 'A partir de uma composição existente', text: 'Copia modelos, envio e variáveis de outra composição do mesmo canal.' },
                        ].map((option) => (
                            <button
                                key={option.key}
                                type="button"
                                onClick={() => setOrigin(option.key)}
                                className={`flex gap-3 text-left rounded-xl border p-4 ${
                                    origin === option.key
                                        ? 'border-teal-500 bg-teal-50/60 dark:bg-teal-900/20'
                                        : 'border-gray-200 dark:border-slate-700 hover:border-gray-300'
                                }`}
                            >
                                <span className="text-teal-600 dark:text-teal-300">{option.icon}</span>
                                <span>
                                    <span className="block text-sm font-medium text-gray-900 dark:text-gray-100">{option.title}</span>
                                    <span className="block text-xs text-gray-500 dark:text-gray-400">{option.text}</span>
                                </span>
                            </button>
                        ))}
                    </div>

                    {origin === 'COPY' && (
                        <div className="mt-4">
                            <SearchFilter value={search} onChange={setSearch} placeholder="Buscar composição..." />
                            <ul className="mt-2 max-h-64 overflow-y-auto divide-y divide-gray-100 dark:divide-slate-700 rounded-lg border border-gray-200 dark:border-slate-700">
                                {candidates.map((m) => (
                                    <li key={m.id}>
                                        <button
                                            type="button"
                                            onClick={() => setSourceId(m.id)}
                                            className={`w-full text-left px-3 py-2 flex items-center gap-3 ${
                                                sourceId === m.id ? 'bg-teal-50 dark:bg-teal-900/30' : 'hover:bg-slate-50 dark:hover:bg-slate-700/50'
                                            }`}
                                        >
                                            <ChannelTile channel={m.channel} size="sm" />
                                            <span className="min-w-0">
                                                <span className="block text-sm text-gray-900 dark:text-gray-100 truncate">{m.description || m.name}</span>
                                                <span className="block text-[11px] font-mono text-gray-400 truncate">{m.name}</span>
                                            </span>
                                        </button>
                                    </li>
                                ))}
                                {!candidates.length && <li className="px-3 py-4 text-sm text-gray-400">Nenhuma composição deste canal.</li>}
                            </ul>
                        </div>
                    )}

                    <div className="flex justify-end mt-5">
                        <button
                            type="button"
                            disabled={!ready}
                            onClick={begin}
                            className="bg-blue-600 hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm font-medium px-5 py-2 rounded-lg"
                        >
                            Começar a editar
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ChannelPicker;
